/**
 * @Description 商圈详情-基本信息栏
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { fixNumber } from '@/common/utils/ways';
import HeatMap from '@/common/components/business/PopulationHeatMap/HeatMap';
import { showMarker } from '@/common/utils/map';
import { industryCodeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import { BisConType, PopulationData } from './ts-config';
import Group from './Group';
import GroupHorizontal from './GroupHorizontal';
import BlockHorizontal from './BlockHorizontal';


const selectedOptions = {
  strokeColor: '#FF861D',
  fillColor: '#FF861D',
  fillOpacity: 0.2,
  strokeWeight: 4
};
const defaultOptions = {
  strokeColor: '#4792FD',
  strokeWeight: 2, // isCustom ? 10 : 2,
  strokeOpacity: 1,
  fillOpacity: 0.06,
  strokeDasharray: [10, 10],
  fillColor: '#006AFF',
};
const Basic: FC<any> = ({
  detail,
  mapIns,
  rangeMarkerRef,
  tenantId,
  setIsOpenHeatMap,
  isShare,
}) => {
  const [active, setActive] = useState<any>({
    keyName: '',
    radius: 0,
    basicParams: {}
  });// 选中的热力

  const populationStr = (count) => {
    if (!isNotEmpty(count)) return '-';
    // return +count >= 10000
    //   ? `${beautifyThePrice(count / 10000.0, ',', 1)}`
    //   : beautifyThePrice(Math.floor(count), ',', 0);
    return +count >= 10000
      ? `${fixNumber(count / 10000, 1)}`
      : Math.floor(count);
  };

  const targetUnit = (count) => {
    if (!isNotEmpty(count)) return '';
    return +count >= 10000 ? '万人' : '人';
  };

  const populationData = useMemo((): PopulationData => {
    const population500m: BisConType = {
      desc: `周边500m居住人口`,
      title: `500m`,
      value: populationStr(detail?.population),
      unit: targetUnit(detail?.population),
      basicValue: detail?.population,
      tag: detail?.population500mRank,
      isHeatMap: true,
      radius: 500,
      keyName: 'population',
      basicParams: {
        queryType: 1,
      }
    };
    const population1500m: BisConType = {
      desc: `周边1.5km居住人口`,
      title: `1.5km`,
      value: populationStr(detail?.population1500m),
      unit: targetUnit(detail?.population1500m),
      basicValue: detail?.population1500m,
      tag: detail?.population1500mRank,
      isHeatMap: true,
      keyName: 'population1500m',
      radius: 1500,
      basicParams: {
        queryType: 1,
      }
    };
    const population3000m: BisConType = {
      desc: `周边3km居住人口`,
      title: `3km`,
      value: populationStr(detail?.population3km),
      basicValue: detail?.population3km,
      unit: targetUnit(detail?.population3km),
      tag: detail?.peopleRank,
      isHeatMap: true,
      radius: 3000,
      keyName: 'population3km',
      basicParams: {
        queryType: 1,
      }
    };
    const populationWork500m: BisConType = {
      desc: `周边500m工作人口`,
      title: `500m`,
      value: populationStr(detail?.populationWork500m),
      unit: targetUnit(detail?.populationWork500m),
      basicValue: detail?.populationWork500m,
      tag: detail?.populationWork500mRank,
      isHeatMap: true,
      radius: 500,
      keyName: 'populationWork500m',
      basicParams: {
        queryType: 2,
      }
    };
    const populationWork1500m: BisConType = {
      desc: `周边1.5km工作人口`,
      title: `1.5km`,
      value: populationStr(detail?.populationWork1500m),
      basicValue: detail?.populationWork1500m,
      unit: targetUnit(detail?.populationWork1500m),
      tag: detail?.populationWork1500mRank,
      isHeatMap: true,
      keyName: 'populationWork1500m',
      radius: 1500,
      basicParams: {
        queryType: 2,
      }
    };
    const populationWork3000m: BisConType = {
      desc: `周边3km工作人口`,
      title: `3km`,
      value: populationStr(detail?.populationWork3000m),
      basicValue: detail?.populationWork3000m,
      unit: targetUnit(detail?.populationWork3000m),
      tag: detail?.populationWork3000mRank,
      isHeatMap: true,
      keyName: 'populationWork3000m',
      radius: 3000,
      basicParams: {
        queryType: 2,
      }
    };
    const residentPopulation500m: BisConType = {
      desc: `周边500m常驻人口`,
      title: `500m`,
      value: populationStr(detail?.residentPopulation500m),
      unit: targetUnit(detail?.residentPopulation500m),
      basicValue: detail?.residentPopulation500m,
      tag: detail?.residentPopulation500mRank,
      isHeatMap: true,
      keyName: 'residentPopulation500m',
      radius: 500,
      basicParams: {
        queryType: 3,
      }
    };
    const residentPopulation1500m: BisConType = {
      desc: `周边1.5km常驻人口`,
      title: `1.5km`,
      value: populationStr(detail?.residentPopulation1500m),
      basicValue: detail?.residentPopulation1500m,
      unit: targetUnit(detail?.residentPopulation1500m),
      tag: detail?.residentPopulation1500mRank,
      isHeatMap: true,
      keyName: 'residentPopulation1500m',
      radius: 1500,
      basicParams: {
        queryType: 3,
      }
    };
    const residentPopulation3000m: BisConType = {
      desc: `周边3km常驻人口`,
      title: `3km`,
      value: populationStr(detail?.residentPopulation3000m),
      unit: targetUnit(detail?.residentPopulation3000m),
      basicValue: detail?.residentPopulation3000m,
      tag: detail?.residentPopulation3000mRank,
      isHeatMap: true,
      keyName: 'residentPopulation3000m',
      radius: 3000,
      basicParams: {
        queryType: 3,
      }
    };

    const showMallFlow = !!detail?.mallPassFlow;
    const passengerFlow: any = showMallFlow ? {
      desc: '商场日均客流',
      title: '商场日均客流',
      value: populationStr(detail?.mallPassFlow),
      unit: targetUnit(detail?.mallPassFlow),
      basicValue: detail?.mallPassFlow,
      tag: detail?.flowRank,
      keyName: 'mallPassFlow',
    } : null;

    const { industryCode } = detail || {};
    // 判断属于汽车行业且有值，才展示有车比例
    const showCarRate = [industryCodeEnum.carSale, industryCodeEnum.carRepair].includes(industryCode) && !!detail?.carRate;
    const carRate: any = showCarRate ? {
      desc: '有车客群比例',
      title: '有车客群比例',
      value: detail?.carRate ? detail?.carRate + '%' : '-',
      basicValue: detail?.carRate,
      tag: detail?.carRateRank,
      keyName: 'carRate',
    } : null;

    const res: PopulationData = {
      carRate: carRate,
      passengerFlow: passengerFlow,
      population: [
        population500m,
        population1500m,
        population3000m,
      ],
      populationWork: [
        populationWork500m,
        populationWork1500m,
        populationWork3000m,
      ],
      residentPopulation: [
        residentPopulation500m,
        residentPopulation1500m,
        residentPopulation3000m,
      ],
    };
    return res;
  }, [detail]);

  /**
   * @description 不展示汽车保有率、与商场客流数据
   */
  const isNoCarNoPassenger = useMemo(() => {
    return populationData && !isNotEmptyAny(populationData.carRate) && !isNotEmptyAny(populationData.passengerFlow);
  }, [populationData]);

  /**
   * @description 展示汽车保有率、或商场客流数据中的至少一个
   */
  const isHasCarOrMall = useMemo(() => {
    return populationData && (isNotEmptyAny(populationData.carRate) || isNotEmptyAny(populationData.passengerFlow));
  }, [populationData]);

  const onHeatEvent = (checked: boolean, item: BisConType) => {
    // 如果是取消，或者数据不存在则取消
    if (!checked || !(item.isHeatMap/* && item?.basicValue*/)) {
      setActive({
        keyName: '',
        basicParams: {},
        radius: 0
      });
      setIsOpenHeatMap(false);
      return;
    }
    setIsOpenHeatMap(true);
    setActive({
      keyName: item.keyName,
      radius: item.radius,
      basicParams: item.basicParams,
    });
  };

  const handleSelectMarker = (radius) => {
    const { markerOf500, markerOf1500, markerOf3000 } = rangeMarkerRef.current || {};
    if (!markerOf500 || !markerOf1500 || !markerOf3000) return;
    markerOf500.circleMarker.setOptions(defaultOptions);
    handleContentMarker(500, markerOf500.contentMarker);
    markerOf1500.circleMarker.setOptions(defaultOptions);
    handleContentMarker(1500, markerOf1500.contentMarker);
    markerOf3000.circleMarker.setOptions(defaultOptions);
    handleContentMarker(3000, markerOf3000.contentMarker);

    if (radius === 500) {
      const { circleMarker, contentMarker } = markerOf500;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(500, contentMarker, true);
    }
    if (radius === 1500) {
      const { circleMarker, contentMarker } = markerOf1500;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(1500, contentMarker, true);
    }
    if (radius === 3000) {
      const { circleMarker, contentMarker } = markerOf3000;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(3000, contentMarker, true);
    }
  };
  const handleContentMarker = (radius, marker, isSelected = false) => {
    let content = '';
    switch (radius) {
      case 500:
        content = '500m(5分钟步行圈)';
        break;
      case 1500:
        content = '1.5km(15分钟生活圈)';
        break;
      case 3000:
        content = '3km(外卖覆盖)';
        break;
    }
    showMarker(marker, <div className={isSelected ? styles.distanceHintOfSelected : styles.distanceHint}>
      {content}
    </div>);
  };

  useEffect(() => {
    handleSelectMarker(active?.radius);
  }, [active.radius]);

  const renderNoCarNoMall = () => {
    return (
      <Row gutter={12}>
        <Col span={8}>
          <Group groupName='居住人口' list={populationData?.population} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={8}>
          <Group groupName='办公人口' list={populationData?.populationWork} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={8}>
          <Group groupName='常驻人口' list={populationData?.residentPopulation} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
      </Row>
    );
  };

  const renderHasCarOrMall = () => {
    return (
      <Row gutter={[8, 8]}>
        <Col span={12}>
          {
            isNotEmptyAny(populationData.passengerFlow) && isNotEmptyAny(populationData.carRate) ? <Row gutter={8}>
              <Col span={12}>
                <BlockHorizontal data={populationData.passengerFlow}/>
              </Col>
              <Col span={12}>
                <BlockHorizontal data={populationData.carRate}/>
              </Col>
            </Row> : isNotEmptyAny(populationData.passengerFlow) ? <BlockHorizontal data={populationData.passengerFlow} iconSrc='https://staticres.linhuiba.com/project-custom/locationpc/selectionmap/icon_population_flow.png'/> : isNotEmptyAny(populationData.carRate) ? <BlockHorizontal data={populationData.carRate} iconSrc='https://staticres.linhuiba.com/project-custom/locationpc/selectionmap/icon_population_car.png'/> : <></>
          }
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='居住人口' list={populationData?.population} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='办公人口' list={populationData?.populationWork} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='常驻人口' list={populationData?.residentPopulation} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <div className={styles.basicContainer}>
        {isNoCarNoPassenger ? renderNoCarNoMall() : <></>}
        {isHasCarOrMall ? renderHasCarOrMall() : <></>}
      </div>
      <HeatMap
        mapIns={mapIns}
        active={active}
        detail={detail}
        tenantId={tenantId}
        isShare={isShare}
      />
    </>
  );
};

export default Basic;
